import React, { useState, useEffect } from 'react'
import {
  Datagrid,
  TextField,
  useTranslate,
  Pagination,
  ListContextProvider,
  useList,
  TextInput,
  BooleanInput,
  TopToolbar,
  ListToolbar,
  SelectInput,
  useNotify,
  useRecordContext,
  useListFilterContext,
  FunctionField,
} from 'react-admin'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { FormattedTextFieldWithStatus } from '../sales/reservation/helper/FormattedTextFieldWithStatus'
import { PENDING, PENDING_BILLING } from '../sales/reservation/logic/common/state'
import { Button, Dialog, DialogContent, DialogTitle, Skeleton, Stack, Typography } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { Visibility } from '@mui/icons-material'
import { HasPermission } from 'src/helper/HasPermission'
import ReactJson from 'react-json-view'
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {TextField as MuiTextField} from '@mui/material'

const RangeDateInput = ({source}) =>{
  const translate = useTranslate()
  const today = (new Date()).toISOString()
  const { setFilters, filterValues,displayedFilters } = useListFilterContext();
  const [fromvalue, setFromValue] = useState(filterValues[`${source}__gte`] ?? today?.split('T')[0])
  const [tovalue, setToValue] = useState(filterValues[`${source}__lte`] ?? today?.split('T')[0])

  useEffect(()=>{
    setFilters({
      ...filterValues,
      [`${source}__gte`]: fromvalue,
      [`${source}__lte`]: tovalue,
    },{...displayedFilters,[`${source}__gte`]:true, [`${source}__lte`]:true});
  },[fromvalue, tovalue])
  return(
    <Stack direction="row" spacing={2} >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
          <DatePicker
            value={fromvalue}
            label={`${translate('ra.field.date_emision')} ${translate('ra.field.from')}`}
            onChange={(date) => setFromValue(date.toISOString()?.split('T')[0])}
            renderInput={(params) => (
              <MuiTextField {...params} fullWidth variant='outlined' size='small' error={false} />
            )}
          />
        </LocalizationProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
        <DatePicker
          value={tovalue}
          minDate={fromvalue || null}
          label={`${translate('ra.field.date_emision')} ${translate('ra.field.to')}`}
          onChange={(date) => setToValue(date.toISOString()?.split('T')[0])}
          renderInput={(params) => (
            <MuiTextField {...params} fullWidth variant='outlined' size='small' error={false} />
          )}
        />
      </LocalizationProvider>
    </Stack>
  )
}

const ShowJsonButton = () => {
  const translate = useTranslate()
  const record = useRecordContext();
  const [openDialog, setOpenDialog] = useState(false)
  const [jsonData, setJsonData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  if (!record) return null;

  const getTicketInfo = async () =>{
    setOpenDialog(true)
    setIsLoading(true)
    await reservationServices.getTicketInfo(Number(record.id.split('_')[0])).then(res=>{
      setJsonData(res.data.xml_response)
    }).finally(()=>{
      setIsLoading(false)
    })
  }

  return (
    <>
      <Visibility
        color="primary"
        fontSize="small"
        style={{ cursor: 'pointer' }}
        onClick={getTicketInfo}
      />
      <Dialog open={openDialog} onClose={()=>setOpenDialog(false)}>
        <DialogTitle>
          <div style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
            {translate('ra.field.json')}
            <CloseIcon onClick={()=>setOpenDialog(false)} style={{cursor:'pointer'}}/>
          </div>
        </DialogTitle>
        <DialogContent>
          {
            isLoading ?
            <Skeleton width={200}/>:
            <ReactJson
              src={jsonData}
              /* theme="monokai" */
              collapsed={2}
              enableClipboard={(copy) => {
                try {
                  navigator.clipboard.writeText(JSON.stringify(copy.src));
                } catch (e) {
                }
              }}
              displayDataTypes={false}
            />
          }
        </DialogContent>
      </Dialog>
    </>
  );
};

function AirStarlingsTicketsList({ refreshData, handleFinishRefresh }) {
  const notify = useNotify()
  const translate = useTranslate()
  const [isLoading, setIsLoading] = useState(false)
  const [filter, setFilter] = useState({})
  const [displayedFilters, setDisplayedFilters] = useState({})
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 })
  const [airStarlingsTickets, setAirStarlingsTickets] = useState([])
  const [timeoutFilters, setTimeoutFilters] = useState()
  const [total, setTotal] = useState(0)
  const [loadingExport, setLoadingExport] = useState(false)

  useEffect(() => {
    getAirStarlingsTickets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, pagination])

  useEffect(() => {
    if (refreshData) {
      getAirStarlingsTickets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData])

  const formatFilterParams = (rawFilter) => {
    const params = { ...rawFilter }

    if (typeof params.supplier_booking_status === 'boolean') {
      if (params.supplier_booking_status) {
        params.supplier_booking_status = 'INVOICED'
        delete params.supplier_booking_status__not_in
      } else {
        params.supplier_booking_status__not_in = 'INVOICED'
        delete params.supplier_booking_status
      }
    }

    return params
  }

  const onExport = async () =>{
    setLoadingExport(true)
    try{
      const params = {
        order_by: '-id',
        skip: 0,
        limit: 1000,
        ...formatFilterParams(filter),
      }
      await reservationServices.exportAirStarlingsTickets(params).then(()=>{
        notify('ra.message.export_success', {type:'success', messageArgs:{email:JSON.parse(localStorage.getItem('user'))?.email}})
      })
      .finally(()=>{
        setLoadingExport(false)
      })
    /* const params = {
        order_by: '-id',
        skip: 1,
        limit: 1000,
        ...filter,
      }
      const airTicketsAux = await reservationServices.getAirStarlingsTickets(params)
      if (airTicketsAux) {
        setExportData(airTicketsAux.data)
      } else {
        setExportData([])
      } */
    } catch (error) {
      /* setExportData([]) */
      notify('ra.message.export_error',{type:'error'})
    }
  }

  const getAirStarlingsTickets = async () => {
    try {
      setIsLoading(true)
      const params = {
        order_by: '-id',
        skip: (pagination.page - 1) * pagination.perPage,
        limit: pagination.perPage,
        ...formatFilterParams(filter),
      }
      const airTicketsAux = await reservationServices.getAirStarlingsTickets(params)
      if (airTicketsAux) {
        setAirStarlingsTickets(airTicketsAux.data.map(item => ({ ...item, id: `${item.id}_${item.rownum}` })))
        setTotal(airTicketsAux.total)
        setIsLoading(false)
      } else {
        setAirStarlingsTickets([])
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      setAirStarlingsTickets([])
    } finally {
      if (refreshData) {
        handleFinishRefresh?.()
      }
    }
  }

  const listContext = useList({
    data: airStarlingsTickets,
    resource: 'air-import',
    isLoading,
    sort: { field: 'id', order: 'DESC' },
    displayedFilters: displayedFilters,
    filterValues: filter,
    ...pagination,
  })

  const onShowFilter = (filterName, defaultValue) => {
    setDisplayedFilters((prev) => ({
      ...prev,
      [filterName]: true,
    }))
    if (defaultValue !== undefined) {
      setFilter((prev) => ({
        ...prev,
        [filterName]: defaultValue,
      }))
      onChangePagination('page', 1)
    }
  }

  const onHideFilter = (filterName) => {
    clearTimeout(timeoutFilters)
    const filterAux = { ...filter }
    const displayedFiltersAux = { ...displayedFilters }
    if (filterName === 'ticketing_date') {
      delete displayedFiltersAux[`${filterName}__gte`]
      delete displayedFiltersAux[`${filterName}__lte`]
      delete displayedFiltersAux[filterName]
      delete filterAux[`${filterName}__gte`]
      delete filterAux[`${filterName}__lte`]
      delete filterAux[filterName]
    } else {
      delete displayedFiltersAux[filterName]
      delete filterAux[filterName]
      if (filterName === 'supplier_booking_status') {
        delete filterAux['supplier_booking_status']
        delete filterAux['supplier_booking_status__not_in']
      }
    }
    setDisplayedFilters(displayedFiltersAux)
    setFilter(filterAux)
    onChangePagination('page', 1)
  }

  const onChangeFilters = (filters, displayedFiltersAux) => {
    setDisplayedFilters(displayedFiltersAux)

    clearTimeout(timeoutFilters)
    const timeout = setTimeout(() => {
      Object.entries(filters).forEach((elem) => {
        const [key, value] = elem
        if (value === null) {
          delete filters[key]
        }
      })

      if (JSON.stringify(filter) !== JSON.stringify(filters)) {
        setFilter(filters)
      }
      onChangePagination('page', 1)
    }, 1000)
    setTimeoutFilters(timeout)
  }

  const onChangePagination = (field, value) => {
    if (pagination[field] !== value) {
      setPagination((current) => ({
        ...current,
        [field]: value,
      }))
      setAirStarlingsTickets([])
    }
  }

  const AirTicketsFilters = [
    <BooleanInput label={translate('ra.field.in_reserve')} source='is_imported' defaultValue={false} />,
    <BooleanInput label={translate('ra.field.invoiced')} source='supplier_booking_status' defaultValue={false} />,
    <TextInput label={translate('ra.field.pnr')} source='pnr_code' defaultValue='' />,
    <TextInput label={translate('ra.field.gds')} source='gds' defaultValue='' />,
    <TextInput label={translate('ra.field.item_ticket_number')} source='pax_item__ticket_number__in' defaultValue='' />,
    <TextInput label={translate('ra.field.passenger')} source='passenger' defaultValue='' />,
    <TextInput
      label={translate('ra.field.reservation_number')}
      source='reservation__number__ilike'
      defaultValue=''
    />,
    <SelectInput
      label={translate('ra.field.token')}
      source='token'
      fullWidth
      choices={[
        { id: 'GTR USD', name: 'GTR USD' },
        { id: 'GTR ARS', name: 'GTR ARS' },
        { id: 'CONEXT USD', name: 'CONEXT USD' },
      ]}
    />,
    <RangeDateInput source={'ticketing_date'} label={`${translate('ra.field.date_emision')}`}/>
  ]
  return (
    <ListContextProvider
      key={`${pagination.page}-${JSON.stringify(filter)}-${JSON.stringify(displayedFilters)}-${airStarlingsTickets.length}`}
      value={{
        ...listContext,
        filterValues: filter,
        displayedFilters: displayedFilters,
        setFilters: onChangeFilters,
        showFilter: onShowFilter,
        hideFilter: onHideFilter,
      }}
    >
      <TopToolbar>
        <ListToolbar
          actions={
            <ListViewTopToolbar
              exporterDisable
              CustomExportButton={()=>(
                <Button
                  sx={{ lineHeight: '1.5'}}
                  size='small'
                  startIcon={<FileDownloadIcon />}
                  onClick={onExport}
                  disabled={listContext.isLoading || loadingExport}
                >
                  Exportar
                </Button>
              )}
            />
          }
          filters={AirTicketsFilters}
        />
      </TopToolbar>

      <Datagrid isLoading={isLoading} bulkActionButtons={null}>
        <FormattedTextFieldWithStatus
          label={translate('ra.field.status_b2')}
          source='status'
          sortable={false}
          getValue={(value) => (value === PENDING ? PENDING_BILLING : value)}
        />
        <FunctionField
          label={translate('ra.field.emission_status')}
          source='supplier_booking_status'
          sortable={false}
          render={(record) => {
            if (!record?.supplier_booking_status) return null
            return record.supplier_booking_status === 'INVOICED'
              ? translate('ra.status.INVOICED')
              : translate('ra.status.NOT_INVOICED')
          }}
        />
        <TextField label={translate('ra.field.pnr')} source='pnr_code' sortable={false} />
        <TextField label={translate('ra.field.gds')} source='gds' sortable={false} />
        <TextField label={translate('ra.field.ticket_number')} source='ticket_number' sortable={false} />
        <TextField label={translate('ra.field.ticket_type')} source='ticket_type' sortable={false} />
        <TextField label={translate('ra.field.date_emision')} source='ticketing_date' sortable={false} />
        <TextField label={translate('ra.field.passenger')} source='passenger' sortable={false} />
        <FunctionField
          label={translate('ra.field.reservation_number')}
          source='number'
          sortable={false}
          render={(record) => {
            if (!record?.number && record?.number !== 0) return null
            if (!record?.reservation_id) return record.number
            return (
              <Typography
                component='a'
                href={`/#/reservation/${record.reservation_id}/show`}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: '#3f51b5',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {record.number}
              </Typography>
            )
          }}
        />
        <TextField label={translate('ra.field.token')} source='token' sortable={false} />
        <HasPermission permission='show' resource={'air-import'}>
          <ShowJsonButton/>
        </HasPermission>
      </Datagrid>

      <Pagination
        {...pagination}
        total={total}
        isLoading={isLoading}
        setPage={(value) => onChangePagination('page', value)}
        setPerPage={(value) => onChangePagination('perPage', value)}
      />
    </ListContextProvider>
  )
}

export default AirStarlingsTicketsList
